import {
  FlightInformation,
  getFlightDetails
} from "../../ShellCommon/eventModule/window-communication-bridge-flighting";
import { entries } from "./local-storage-wrapper-service";

let localWindow = <LocalStorageWindow>window;
export let useCachedLocalStorageKeys = true;
let useLocalStorageKeyApi = false;
localWindow.localStorageKeys = localWindow.localStorageKeys || {};
export let overrideUseLocalStorageKey: boolean = undefined;
let overrideUseLocalStorageKeyApi: boolean = undefined;

export interface LocalStorageWindow {
  useCachedLocalStorageKeys?: boolean;
  localStorageKeys?: any;
  testData?: Array<any>;
}

export let shouldUseCachedLocalStorageKeys = () => {
  if (overrideUseLocalStorageKey != undefined) {
    useCachedLocalStorageKeys = overrideUseLocalStorageKey;
    return overrideUseLocalStorageKey;
  }
  getFlightDetails().then((flightDetails) => {
    useCachedLocalStorageKeys = flightDetails.useCachedLocalStorageKeys;
  });
  return useCachedLocalStorageKeys;
};

export let shouldUseLocalStorageKeyApi = () => {
  if (overrideUseLocalStorageKeyApi != undefined) {
    return overrideUseLocalStorageKeyApi;
  }
  getFlightDetails().then((flightDetails) => {
    useLocalStorageKeyApi = flightDetails.useLocalStorageKeyApi;
  });
  return useLocalStorageKeyApi;
};

/**
 * Work around to get all localstorage entries caused by issue 13447308
 */
window.addEventListener("storage", (event: StorageEvent) => {
  if (event.storageArea == window.localStorage) {
    // when localStorage.clear called
    if (event.key == "" && event.newValue == "") {
      localWindow.localStorageKeys = {};
      return;
    }

    localWindow.localStorageKeys[event.key] = true;
  }
});

export let getSize = (storage: Storage) => {
  let size = 0;
  entries(storage).forEach((value: string, key: string) => {
    size += key.length + value.length;
  });
  return size;
};

/**
 * Work around to get all localstorage entries caused by issue 13447308
 * @param storage window.localstorage
 */
export let getCachedKeys = (storage: Storage, checkExists?: boolean): Set<string> => {
  if (shouldUseCachedLocalStorageKeys()) {
    return getStorageKeys(storage, checkExists);
  }
  disable();

  return getKeysInternal(storage, checkExists);
};

/**
 * Work around to get all localstorage entries caused by issue 11068047 & 13447308
 * @param storage window.localstorage
 */
export let cachedEntries = (storage: Storage): Map<string, string> => {
  if (shouldUseCachedLocalStorageKeys()) {
    return cachedKeyEntries(storage);
  }
  disable();

  return entriesInternal(storage);
};

export let getKeysInternal = (storage: Storage, checkExists?: boolean): Set<string> => {
  let keys = new Set<string>();
  for (let i = 0; i < storage.length; i++) {
    keys.add(storage.key(i));
  }
  return keys;
};

/**
 * Work around to get all localstorage entries caused by issue 11068047
 * @param localStorage window.localstorage
 */
export let entriesInternal = (storage: Storage): Map<string, string> => {
  let entries = new Map<string, string>();
  for (let i = 0; i < storage.length; i++) {
    let key = storage.key(i);
    entries.set(key, storage.getItem(key));
  }
  return entries;
};

let localWindowInternalCached = <LocalStorageWindow>window;
let getKeysTimer: any;

let cachedKeyHasValue = (storage: Storage, key: string): boolean => {
  return !(storage.getItem(key) == undefined);
};

/**
 * Work around to get all localstorage entries caused by issue 13447308
 * @param storage window.localstorage
 */
export let syncLocalStorageKeys = (storage: Storage, localWindow: LocalStorageWindow) => {
  if (!useCachedLocalStorageKeys) {
    disable();
    return;
  }

  if (shouldUseLocalStorageKeyApi()) {
    synLocalStorageKeysByKeyApi(storage, localWindow);
  } else {
    synLocalStorageKeysByKeyExistenceCheck(storage, localWindow);
  }
};

let synLocalStorageKeysByKeyApi = (storage: Storage, localWindow: LocalStorageWindow) => {
  let length: Number = ((localWindow.localStorageKeys && localWindow.localStorageKeys.length) || 0) + storage.length;
  let counter = 0;
  let tempKeys: any = {};
  while (counter < length) {
    let key = storage.key(counter);
    if (key == undefined) {
      break;
    }
    tempKeys[key] = true;
    localWindow.localStorageKeys[key] = true;
    counter++;
  }
  localWindow.localStorageKeys = tempKeys;
};

let synLocalStorageKeysByKeyExistenceCheck = (storage: Storage, localWindow: LocalStorageWindow) => {
  let checkedKeys: any = {};
  Object.keys(localWindow.localStorageKeys).forEach((key: string) => {
    if (!cachedKeyHasValue(storage, key)) {
      delete localWindow.localStorageKeys[key];
    }
    checkedKeys[key] = true;
  });

  Object.keys(storage).forEach((key: string) => {
    if (checkedKeys[key] || cachedKeyHasValue(storage, key)) {
      localWindow.localStorageKeys[key] = true;
    }
  });

  if (Object.keys(localWindow.localStorageKeys).length != storage.length) {
    let localStorageKeys: any = {};
    for (let i = 0; i < storage.length; i++) {
      let key = storage.key(i);
      localStorageKeys[key] = true;
    }
    localWindow.localStorageKeys = localStorageKeys;
  }
};

export let disable = () => {
  if (getKeysTimer) {
    window.clearInterval(getKeysTimer);
    getKeysTimer = undefined;
  }
};

export let getStorageKeys = (storage: Storage, checkExists?: boolean): Set<string> => {
  let keys = new Set<string>();
  if (!getKeysTimer && window == window.top) {
    getKeysTimer = window.setInterval(syncLocalStorageKeys.bind(undefined, storage, localWindow), 1000);
  }
  let keysViaEvent = localWindow.localStorageKeys || {};

  Object.keys(keysViaEvent).map((key: string) => {
    if (!checkExists || cachedKeyHasValue(storage, key)) {
      keys.add(key);
    }
  });

  return keys;
};

/**
 * Work around to get all localstorage entries caused by issue 11068047 & 13447308
 * @param storage window.localstorage
 */
export let cachedKeyEntries = (storage: Storage): Map<string, string> => {
  let entries = new Map<string, string>();
  let keys = getStorageKeys(storage);
  keys.forEach((key) => {
    let item = storage.getItem(key);
    if (cachedKeyHasValue(storage, key)) {
      entries.set(key, item);
    }
  });

  return entries;
};
